import React from 'react'
import {
  Alert,
  Button,
  Group,
  List,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'

import {
  ScriptBlock,
  scriptStyles,
} from '@/components/Event/CrForm/components/ScriptBlock'
import { StateScriptBlock } from '@/components/Event/CrForm/components/StateScriptBlock'
import {
  CrFormStep,
  witnessStepKey,
} from '@/components/Event/CrForm/CrFormStep'
import {
  getScriptValues,
  getStateRecordScript,
} from '@/components/Event/CrForm/stateRecordScripts'
import { Step } from '@/components/Event/CrForm/Step'
import { useRegisterStepFields } from '@/components/Event/CrForm/useStepField'
import { Card } from '@/components/Event/Detail/components/Card'
import { WitnessAttendees } from '@/components/Event/Feedback/WitnessAttendees'
import { useFormattedCurrentDateParts } from '@/components/Event/NewScript/hooks/useFormattedCurrentDateParts'
import { Event, Reporter, ReporterFeedback } from '@/types'
import { shouldSaTakeTranscriptOrders } from '@/utils/parrot'
import { getEventTimezone } from '@/utils/time'

// announce_record binds to custom Yes/No buttons (not an input that owns registration),
// so register its path with the enclosing step here — otherwise the Attendance step's
// status circle wouldn't reflect an unanswered question.
// Must render inside the Step so useRegisterStepFields sees the step's registry context.
// Paths are module constants so the array reference stays stable across renders.
const ANNOUNCE_RECORD_PATH = ['sa.announce_record']
const RegisterStepFields: React.FC<{ paths: string[] }> = ({ paths }) => {
  useRegisterStepFields(paths)
  return null
}

// Attendance section: CR intro script, announce-record question, and attendees box.
// CR presence (sa.cr_joined) lives on PreparationStep.
export const AttendanceStep: React.FC<{
  event: Event
  reporter: Reporter
  form: UseFormReturnType<ReporterFeedback>
  // Attendees are per-witness; the box shows the active witness's list.
  witnessIndex: number
  onAddWitness: () => void
  addWitnessDisabled: boolean
}> = ({
  event,
  reporter,
  form,
  witnessIndex,
  onAddWitness,
  addWitnessDisabled,
}) => {
  const announceRecord = form.values.sa?.announce_record ?? null
  const announceRecordError = form.errors['sa.announce_record']
  const dt = useFormattedCurrentDateParts(getEventTimezone(event))
  // CA and WA SAs always call the record, so the announce question doesn't apply.
  const stateScript = getStateRecordScript(event)
  const scriptValues = getScriptValues(event, reporter, dt)

  return (
    <Step
      id={CrFormStep.ATTENDANCE}
      title="Attendance"
      form={form}
      nextStepId={witnessStepKey(CrFormStep.OPENING, witnessIndex)}
    >
      <Stack>
        <Alert title="Reminder">
          The Court Reporter owns the official record. As a neutral third party,
          defer to them on all matters concerning the record.
        </Alert>

        {stateScript ? (
          stateScript.crIntroduction && (
            <StateScriptBlock
              heading="Introduction to the Court Reporter"
              lines={stateScript.crIntroduction}
              values={scriptValues}
            />
          )
        ) : (
          <>
            <ScriptBlock heading="Introduction to the Court Reporter">
              Hello, I am {reporter.name} the Session Assistant for Filevine.
              I&apos;ll be managing the Zoom session today.
              <br />
              <br />I want to confirm that you have everything you need and
              whether or not you would like me to announce when we go on and off
              the record to allow you to focus fully on preparing the record.
            </ScriptBlock>

            <RegisterStepFields paths={ANNOUNCE_RECORD_PATH} />
            <Stack spacing={4}>
              <Text size="sm" fw={600}>
                Did the Court Reporter ask you to announce going on and off the
                record?{' '}
                <Text component="span" color="red">
                  *
                </Text>
              </Text>
              <Group
                spacing="sm"
                mt="xs"
                align="center"
                data-invalid={announceRecordError ? 'true' : undefined}
              >
                <Button
                  variant={announceRecord === true ? 'filled' : 'default'}
                  onClick={() => form.setFieldValue('sa.announce_record', true)}
                >
                  Yes
                </Button>
                <Button
                  variant={announceRecord === false ? 'filled' : 'default'}
                  onClick={() =>
                    form.setFieldValue('sa.announce_record', false)
                  }
                >
                  No
                </Button>
              </Group>
              {announceRecordError && (
                <Text size="sm" color="red" mt={4}>
                  {announceRecordError}
                </Text>
              )}
            </Stack>
          </>
        )}

        <Textarea
          label="Additional Record Preference Details"
          description="Record the Court Reporter's stated preferences."
          minRows={3}
          autosize
          {...form.getInputProps('sa.record_preferences')}
        />

        <Card heading="Filevine mandatory policy">
          <Text size="sm" color="gray.9" sx={scriptStyles}>
            I also want to give a quick heads-up. Filevine has mandatory
            national policies that might seem redundant to your state&apos;s
            rules. While we fully respect that you own the official record, I am
            required by my company to collect the following solely for the case
            management portion of our software in the event that you do not:
          </Text>
          <List
            type="ordered"
            withPadding
            mt="xs"
            sx={scriptStyles}
            style={{ listStyle: 'ordered' }}
          >
            <List.Item>Confirm spellings and contact information.</List.Item>
            <List.Item>Spotlight the witness.</List.Item>
            <List.Item>
              Confirm if the witness will read and sign, or reserve their
              signature.
            </List.Item>
            {shouldSaTakeTranscriptOrders(event) && (
              <List.Item>
                Log any transcript orders placed through you.
              </List.Item>
            )}
          </List>
        </Card>

        {stateScript ? (
          <StateScriptBlock
            heading="Introduction to attendees"
            lines={stateScript.attendeesIntroduction}
            values={scriptValues}
          />
        ) : (
          <ScriptBlock heading="Introduction to attendees">
            Hello, I am {reporter.name} the Session Assistant for Filevine.
            I&apos;ll be managing the Zoom session today.
            <br />
            <br />
            Please let me know if I can be of any assistance; if needed I am
            able to place you in a breakout room.
          </ScriptBlock>
        )}

        <Text size="sm" fw={600}>
          Record each attendee&apos;s details below, confirming the full
          spelling of names and email addresses.
        </Text>

        <WitnessAttendees
          event={event}
          form={form}
          witnessIndex={witnessIndex}
          crLayout
          onAddWitness={onAddWitness}
          addWitnessDisabled={addWitnessDisabled}
        />
      </Stack>
    </Step>
  )
}
